import { timerHelperModule } from '../../../../shared/app/helpers/timer-helper';
import { accountServiceModule } from '../../../../shared/app/services/account-service/account-service';
import { assessmentResultsServiceModule } from '../../services/assessment-results-service/assessment-results-service';
import { liveInterviewServiceModule } from '../../../../shared/app/services/live-interview-service/live-interview-service';
import { assessmentTimerServiceModule } from '../../services/assessment-timer-service/assessment-timer-service';
import { appAlertComponentModule } from '../../../../shared/app/components/app-alert/app-alert';
import { dialogComponentModule } from '../../../../shared/app/components/dialog/dialog';
import { scrollToComponentModule } from '../../../../shared/app/components/scroll-to/scroll-to';
import { assessmentSubmittingDialogComponentModule } from '../../components/assessment-submitting-dialog/assessment-submitting-dialog';
import { solutionModelModule } from '../../../../shared/app/models/solution';
import {
  assessmentResultModelModule,
  EDIT_MODE_NORMAL,
  EDIT_MODE_READONLY,
  EDIT_MODE_RESTRICTED,
} from '../../../../shared/app/models/assessment-result';
import { windowEventHelperModule } from '../../../../shared/app/helpers/window-event-helper/window-event-helper';
import { LOGOUT_EJECT_EXCLUDES } from '../../config/account-service';
import { socketServiceModule } from '../../../../shared/app/services/socket-service/socket-service';
import { firebaseServiceModule } from '../../../../shared/app/services/firebase/firebase';
import {
  ERROR_CONTEXT,
  messageHubServiceModule,
} from '../../services/message-hub-service/message-hub-service';
import { safeUrlFilterModule } from '../../../../shared/app/filters/safe-url-filter';
import { idleDetectionHelperModule } from '../../../../shared/app/helpers/idle-detection-helper/idle-detection-helper';
import { appConfig } from '../../../../shared/app/constants/app-config';
import { warnBeforeClosing } from '../../../../shared/app/utils/dom-utils';
import { editProfileDialogComponentModule } from '../../components/edit-profile-dialog/edit-profile-dialog';
import { embedded } from '../../config/embedded';
import { trackerServiceModule } from '../../../../shared/app/services/tracker-service/tracker-service';
import { shouldShowAssessmentTimer } from './assessment-timer-visibility';

export const STAGE_STATUSES = {
  NEW: 'new',
  PARTIAL: 'partial',
  COMPLETED: 'completed',
  EXPIRED: 'expired',
  FAILED: 'failed',
};

const STAGE_LABEL = {
  new: 'Not Started',
  partial: 'Incomplete',
  completed: 'Ready for Scoring',
  nonChallengeCompleted: 'Completed',
  closedCompleted: 'Scored',
  expired: 'No Longer Editable',
  failed: 'Error on Run',
};

export const assessmentResultProcessServiceModule = angular
  .module('Q.routes.assessments.assessment-result-process-service', [
    timerHelperModule,
    accountServiceModule,
    assessmentResultsServiceModule,
    liveInterviewServiceModule,
    assessmentTimerServiceModule,
    appAlertComponentModule,
    dialogComponentModule,
    scrollToComponentModule,
    assessmentSubmittingDialogComponentModule,
    solutionModelModule,
    assessmentResultModelModule,
    windowEventHelperModule,
    socketServiceModule,
    firebaseServiceModule,
    messageHubServiceModule,
    safeUrlFilterModule,
    idleDetectionHelperModule,
    editProfileDialogComponentModule,
    trackerServiceModule,
  ])

  .factory(
    'assessmentResultProcess',
    function (
      $rootScope,
      Solution,
      assessmentResultsService,
      assessmentTimerService,
      accountService,
      $state,
      $q,
      appAlert,
      liveInterviewService,
      timerHelper,
      AssessmentResult,
      dialogService,
      scrollToElement,
      $log,
      $document,
      $filter,
      assessmentSubmittingDialog,
      $interval,
      $timeout,
      allModels,
      socketService,
      firebase,
      $window,
      messageHubService,
      embedOptions,
      safeUrl,
      editProfileDialog,
      configService,
      tracker,
    ) {
      return {
        create,
      };

      /**
       * Creates a Stage object used to track the current stage of the application process.
       *
       * @param state Required state id (for tracking current/next)
       * @param stateParams Optional state params to pinpoint current/next
       * @param assessmentResult
       * @constructor
       */
      function Stage(state, stateParams, assessmentResult) {
        this.state = state;
        this.stateParams = stateParams;
        this.status = STAGE_STATUSES.NEW;
        this.isCompleted = () =>
          this.status === STAGE_STATUSES.COMPLETED ||
          this.status === STAGE_STATUSES.EXPIRED;
        this.label = () => {
          if (this.status === STAGE_STATUSES.COMPLETED) {
            if (!assessmentResult) {
              return STAGE_LABEL.nonChallengeCompleted;
            } else if (assessmentResult.isClosed()) {
              return STAGE_LABEL.closedCompleted;
            }
          }
          return STAGE_LABEL[this.status];
        };
        this.tooltip = () =>
          this.status === 'new' ? '' : ` (${STAGE_LABEL[this.status]})`;
      }

      /**
       * Creates an assessment result process object based on the given assessment.
       *
       * This object can be used to determine information about the current assessment result, such as number of
       * challenge stages, and—once the user has logged in—status of each stage.
       *
       * @param {Object} auth - Authorization data acquired from the API
       * @param {number} heartbeatInterval - The number of seconds between each heartbeat
       * @returns {{finished: boolean, assessmentResult: null, assessment: *, team: (*|.modes.team|qualified.models.team), stages: {welcome: Stage, challenges: Array, review: Stage}}}
       */
      function create(auth, heartbeatInterval) {
        const { assessment } = auth;
        const { CountDownTimer, CountUpTimer } = assessmentTimerService;

        //=======================================================
        // SETUP
        //=======================================================

        const stages = {
          welcome: new Stage('assessments.welcome'),
          challenges: [],
          review: new Stage('assessments.review'),
        };

        const highlights = { timer: false, stages: false };

        // TODO: clarify why these are needed in the context of loading/readiness stages
        // In brief, the assessmentResult property is loaded asynchronously
        // on the assessmentResultProcess. whenAssessmentResultReady() can
        // be used to wait for it.
        const readyDeferred = $q.defer();
        let loadAssessmentResultPromise = null;
        const assessmentResultDeferred = $q.defer();

        const rootScopeListeners = [];

        const process = {
          get canEdit() {
            return (
              process.assessmentResult && process.assessmentResult.canEdit()
            );
          },
          get editMode() {
            let embedMode = this.embedOptions && this.embedOptions.mode;
            const arMode =
              this.assessmentResult && this.assessmentResult.editMode;
            if (
              embedMode === EDIT_MODE_READONLY ||
              arMode === EDIT_MODE_READONLY
            ) {
              return EDIT_MODE_READONLY;
            } else if (
              embedMode === EDIT_MODE_RESTRICTED ||
              arMode === EDIT_MODE_RESTRICTED
            ) {
              return EDIT_MODE_RESTRICTED;
            } else {
              return EDIT_MODE_NORMAL;
            }
          },
          get readOnly() {
            return (
              this.editModeReadOnly ||
              (this.editMode !== EDIT_MODE_RESTRICTED && !this.canEdit)
            );
          },
          get restricted() {
            return this.readOnly || this.editModeRestricted;
          },
          get editModeReadOnly() {
            return this.editMode === EDIT_MODE_READONLY;
          },
          get editModeRestricted() {
            return (
              this.editModeReadOnly || this.editMode === EDIT_MODE_RESTRICTED
            );
          },
          isFinished() {
            return (
              process.assessmentResult && process.assessmentResult.isClosed()
            );
          },
          get significantTimeDelay() {
            return (
              configService.config.significantTimeDelay &&
              (assessment.enforcedTimeLimit || this.hasTimedChallenges) &&
              !this.isFinished() &&
              !this.invitationIssue
            );
          },
          isFinishedOrInvalid() {
            return (
              this.invitationIssue ||
              this.isFinished() ||
              this.significantTimeDelay
            );
          },
          isSafeToSubmit() {
            const attemptedAll = process.stages.challenges.every(
              (s) => s.status !== STAGE_STATUSES.NEW,
            );
            return attemptedAll || process.isTeamMember() || process.isSample();
          },
          isFullyComplete() {
            return (
              process.assessmentResult &&
              (process.isSample() ||
                stages.challenges.every((s) => s.isCompleted()))
            );
          },
          logout: () => {
            if (process.timer && process.timer.isRunning()) {
              dialogService
                .confirm(
                  'There is an active timer on this assessment which will not stop when you logout. Are you sure you want to continue?',
                  'Active Timer',
                )
                .then(({ id }) => {
                  if (id === 'yes') {
                    accountService.logoutAndReload();
                  }
                });
            } else {
              accountService.logoutAndReload();
            }
          },
          liveInterview: {},
          assessment,
          stages,
          timer: null,
          /**
           * Whether the sidebar assessment timer should be displayed. Hidden while the candidate
           * is inside a challenge showing its own countdown, unless the assessment is the
           * deadline that will hit first.
           */
          get showTimer() {
            return (
              !this.restricted &&
              shouldShowAssessmentTimer({
                timer: this.timer,
                stage: getCurrentStage(),
              })
            );
          },
          auth,
          get invitationIssue() {
            return !!this.auth.denyReason || this.unableToLoadAssessmentResult;
          },
          configureCountDownTimer(timeAllotted) {
            process.timer = new CountDownTimer(timeAllotted);
            process.timer.on('done', _timerRanOut);
            process.timer.on('warning', (timer, seconds) => {
              appAlert.warning(
                `Warning! Less than ${Math.ceil(seconds / 60)} minutes remaining. Finish up any last work.`,
              );
            });
            process.configureTimerDisplay();
          },
          configureCountUpTimer(estimatedTime) {
            process.timer = new CountUpTimer(estimatedTime);
            process.configureTimerDisplay();
          },
          background: false,
          configureTimerDisplay() {
            let title = $document.querySelector('head title');
            let timeMatcher = /^\S+ \d+:\d\d:\d\d - /;
            let secondsToTime = $filter('secondsToTime');
            on('document:focus', () => {
              title.text(title.text().replace(timeMatcher, ''));
            });
            process.timer.on('tick', () => {
              if (process.background && !process.isFinished()) {
                // if warned, shows alarm clock emoji, otherwise a stopwatch emoji
                let icon = process.timer.warned ? '\u23F0 ' : '\u23F1 ';
                title.text(
                  icon +
                    secondsToTime(process.timer.time) +
                    ' - ' +
                    title.text().replace(timeMatcher, ''),
                );
              }
            });
          },
          goToReview: () => $state.go('assessments.review'),
          highlight(component, duration = 3) {
            if (highlights[component])
              timerHelper.longTimeout.cancel(highlights[component]);
            scrollToElement({
              target: `.assessment-sidebar .assessment-nav .${component}`,
            }).then(() => {
              highlights[component] = timerHelper.longTimeout(() => {
                highlights[component] = false;
              }, duration * 1000);
            });
          },
          isHighlighting(component) {
            return component
              ? !!highlights[component]
              : Object.keys(highlights).some((k) => !!highlights[k]);
          },
          stopHighlight() {
            angular.forEach(highlights, (v, k) => {
              if (highlights[k]) {
                timerHelper.longTimeout.cancel(highlights[k]);
                highlights[k] = false;
              }
            });
          },
          challengeCount: 0,
          estimatedTime: assessment.estimatedTime(),
          enforcedTimeLimit: !!assessment.enforcedTimeLimit,
          hasTimedChallenges: assessment.hasTimedChallenges(),
          allStages,
          getCurrentStage,
          addSolution,
          next,
          previous,
          nextIsChallenge,
          nextLabel() {
            return nextIsChallenge()
              ? 'Next Challenge'
              : `Review ${assessment.nounUpper || 'Assessment'}`;
          },
          nextActionLabel() {
            return nextIsChallenge()
              ? 'to the next challenge'
              : `to review the ${assessment.nounLower || 'assessment'}`;
          },
          startAssessmentResult,
          loadAssessmentResult,
          get loadingAssessmentResult() {
            return !!loadAssessmentResultPromise;
          },
          refreshStageStatuses,
          destroy,
          submitAssessment,
          whenReady,
          whenAssessmentResultReady,
          isTeamMember() {
            return auth && !!auth.testTeamMember;
          },
          isPractice() {
            return assessment && assessment.isPractice;
          },
          isDemo() {
            return assessment && assessment.isDemo;
          },
          isSample() {
            return assessment && assessment.isSample;
          },
          get challengeItems() {
            return process.assessmentResult
              ? process.assessmentResult.getChallengeItems()
              : assessment.challengeItems;
          },
          hasCodeChallenges() {
            return process.challengeItems.some(
              (ci) => !ci.challenge || ci.challenge.isCode(),
            );
          },
          get isEmbedded() {
            return messageHubService.embedded;
          },
          restart,
          reset,
          performPostAssessmentAction,
          editProfile: editProfileDialog.open,
        };

        const warnBeforeClosingListener = warnBeforeClosing(() => {
          // warn if the assessment result should be submitted before closing
          const shouldWarn =
            accountService.get().verified &&
            process.canEdit && // can't make changes
            !process.restricted && // can't make changes
            !process.isTeamMember() && // doesn't matter for team members
            !process.isSample() && // or demo or practice assessments
            !process.isEmbedded; // let the parent window deal with it for embeds
          if (shouldWarn) {
            return 'Your assessment is not submitted yet.';
          }
        });

        if (!process.invitationIssue) {
          // configure mock timer for welcome
          if (assessment.enforcedTimeLimit) {
            process.timer = new CountDownTimer(
              assessment.enforcedTimeLimit * 60,
            );
          } else if (process.estimatedTime) {
            process.timer = new CountUpTimer(process.estimatedTime * 60);
          }
        }

        // adding a function to each of the one-off stages to refresh its status
        // these are usually called logging in & out, and whenever we switch stages
        stages.welcome.refresh = function () {
          this.status = process.assessmentResult
            ? STAGE_STATUSES.COMPLETED
            : STAGE_STATUSES.NEW;
        };
        stages.review.refresh = function () {
          this.status = process.isFinished()
            ? STAGE_STATUSES.COMPLETED
            : STAGE_STATUSES.NEW;
        };

        //=======================================================
        // Embedding
        //=======================================================

        const postLoaded = () => {
          messageHubService.postLoaded({
            assessment: process.assessment,
            assessmentResult: process.assessmentResult,
          });
        };

        readyDeferred.promise.then(postLoaded);

        assessmentResultDeferred.promise.then(
          postLoaded,
          messageHubService.postLoadError,
        );

        process.postUpdated = (challenge, stateInfo) => {
          messageHubService.postUpdated({
            assessment: process.assessment,
            assessmentResult: process.assessmentResult,
            challenge,
            stateInfo,
          });
        };
        process.postSolutionUpdated = messageHubService.postSolutionUpdated;
        process.postSubmitted = messageHubService.postSubmitted;
        process.embedOptions = embedOptions;

        messageHubService.onNext = process.next;
        messageHubService.onPrevious = process.previous;
        messageHubService.onSwitchChallenge = (challengeId) => {
          let c = stages[challengeId] || allStages()[challengeId];
          if (!c) {
            // try to go by challenge ID
            c = stages.challenges.find(
              (stage) => stage.challengeId === challengeId,
            );
          }
          if (c) {
            $state.go(c.state, c.stateParams);
          } else {
            throw new Error(
              'Unable to find challenge by index or id: ' + challengeId,
            );
          }
        };
        messageHubService.onSubmit = () => {
          submitAssessment();
        };

        //=======================================================
        // EVENTS
        //=======================================================

        accountService.whenReady().then(function (account) {
          const promise = account.verified
            ? loadAssessmentResult()
            : $q.resolve();
          promise
            .catch(() => null)
            .then((assessmentResult) => {
              readyDeferred.resolve(process);

              // resolve assessmentResult because there's no way to continue if we can't show the welcome screen
              if (!assessmentResult && embedded && embedOptions.hideWelcome) {
                process.unableToLoadAssessmentResult = true;
                assessmentResultDeferred.resolve(null);
              }
            });
        });

        if (!auth.assessmentResult) {
          on('app.accountSet', () => {
            if (!process.assessmentResult && !loadAssessmentResultPromise) {
              process.assessmentLoaded = false;
              loadAssessmentResult();
            }
          });
        }

        // initial setup of challenge stages
        setupChallengeStages();

        on('app.logout', ($event) => ($event.reload = true));

        // update stages when the user manually clicks to switch
        on('$stateChangeSuccess', refreshStageStatuses);

        // track previous presence data sent
        let previousPresenceData = null;

        //=======================================================
        // RETURN OBJECT
        //=======================================================

        return process;

        //=======================================================
        // FUNCTIONS
        //=======================================================

        /**
         * Enters or updates presence on the active candidates team channel, which can be used
         * within Hire app to understand who is actively using the platform
         */
        function setPresence() {
          const user = accountService.get().user || {};
          const presenceData = {
            assessmentId: process.assessment.id,
            assessmentTitle: process.assessment.title,
            assessmentResultId:
              process.assessmentResult && process.assessmentResult.id,
            accessLocked:
              process.assessmentResult && process.assessmentResult.accessLocked,
            isTeamMember: process.isTeamMember(),
            isEmbedded: process.isEmbedded,
            name: user.name,
            email: process.auth.email,
            isActiveInteractSession:
              process.assessmentResult &&
              process.assessmentResult.isActiveInteractSession,
          };

          // try to minimize duplicate requests that could cause Ably message usage explosions
          if (previousPresenceData !== JSON.stringify(presenceData)) {
            socketService.enterPresence(
              `candidates:${process.assessment.teamId}`,
              presenceData,
            );
            previousPresenceData = JSON.stringify(presenceData);
          }
        }

        /**
         * Marks the user as no longer present
         */
        function leavePresence() {
          socketService.leavePresence(
            `candidates:${process.assessment.teamId}`,
          );
        }

        /**
         * After the user has logged in (or been verified by other means), we need to configure the
         * AssessmentResult and the stages for this current assessment app.
         */
        function loadAssessmentResult() {
          // no need to do this twice, or if the result is not present yet
          if (process.assessmentLoaded || !auth.assessmentResult) {
            // Attempting to log a rare issue where assessment result is null with no deny reason, in order to take future action when it comes up again.
            if (
              !auth.assessmentResult &&
              !auth.denyReason &&
              !auth.registrationRequired &&
              !auth.emailVerificationRequired
            ) {
              $log.error(`Assessment Result is null, auth info: ${auth}`, auth);
            }
            return $q.resolve(process.assessmentResult);
          }

          if (assessment.unavailable) {
            assessmentResultDeferred.resolve(null);
            return $q.resolve();
          }
          if (loadAssessmentResultPromise) return loadAssessmentResultPromise;

          let user = accountService.get().user;
          // set this here, just in case the registration process updated the auth after the initial initiation code was set.
          process.assessmentResult = auth.assessmentResult;
          loadAssessmentResultPromise = messageHubService
            .authorize(process.assessment)
            .catch((err) => {
              $state.go('error');
              return $q.reject(err, { error: err });
            })
            .then(() => {
              const assessmentResult = process.assessmentResult;
              if (!assessmentResult) return null;

              if (embedded) {
                // track that this assessment result was embedded
                tracker.trackEvent('Assessment Embedded', {
                  id: assessmentResult.id,
                  assessmentResultId: assessmentResult.id,
                  assessmentId: assessment.id,
                  assessmentTitle: assessment.title,
                  teamId: assessmentResult.teamId,
                  team: assessment.team && assessment.team.name,
                });
                if (!assessmentResult.wasEmbedded) {
                  // store back on the assessment result so we can show this if we want
                  assessmentResultsService.update(assessmentResult, {
                    wasEmbedded: true,
                  });
                }
              }

              // listen for changes to the assessment result object, in case it was closed externally
              watch(
                () => assessmentResult.state,
                () => {
                  if (process.isFinished()) {
                    // jump to the final screen if we've been closed
                    next();
                  }
                },
              );

              // we want to send a fresh heartbeat on each stage change to ensure the activeTimedSolutionId gets set as quickly as possible
              watch(process.getCurrentStage, _heartbeat);

              watchCollection(
                () => [
                  assessmentResult.isActiveInteractSession,
                  process.wasAccessLocked,
                  process.liveInterviewActive,
                ],
                setPresence,
              );

              process.wasAccessLocked = assessmentResult.accessLocked;

              const { enforcedTimeLimit } = assessmentResult;
              const { estimatedTime } = process;

              if (enforcedTimeLimit && !process.invitationIssue) {
                process.configureCountDownTimer(enforcedTimeLimit * 60);
              } else if (estimatedTime && !process.invitationIssue) {
                process.configureCountUpTimer((estimatedTime || 0) * 60);
              } else {
                // clear dummy timer
                process.timer = null;
              }

              if (assessmentResult.isStarted()) {
                startTiming();
              }

              setupChallengeStages();
              refreshStageStatuses();
              assessmentResultDeferred.resolve(assessmentResult);

              socketService.create(accountService.get());

              process.liveInterview = liveInterviewService.join(
                assessmentResult,
                user,
              );

              watch(
                () => process.liveInterview.usersList.length,
                (length) => {
                  if (length > 1 && !process.liveInterviewActive) {
                    process.liveInterviewActive = true;
                    const user = process.liveInterview.usersList.find(
                      (u) => !u.$self,
                    );
                    appAlert.info(
                      `${user.name} has connected to start an interactive coding session.`,
                    );
                    $rootScope.$broadcast('liveInterviewStarted');
                  }
                },
              );

              const checkInteractive = () => {
                if (
                  assessmentResult.isActiveInteractSession &&
                  !process.liveInterviewActive
                ) {
                  firebase.ifConnected().catch((err) => {
                    process.unableToConnect = err;
                    appAlert.error(
                      `Unable to start an interactive session. "${(err && err.message) || err || 'Unknown Error'}"`,
                      {
                        linkTitle: 'Click here for help',
                        linkAction: () => {
                          $window.open(
                            `${appConfig.docsUrl}/for-candidates/interact-issues`,
                          );
                        },
                      },
                    );
                  });
                }
              };
              checkInteractive();
              watch(
                () => assessmentResult.isActiveInteractSession,
                checkInteractive,
              );
              process.assessmentLoaded = true;
              return assessmentResult;
            })
            .catch((err) => {
              if ($window.Sentry) {
                $window.Sentry.setContext({
                  error: err,
                  cookie: $document.cookie,
                });
              }
              let reason = '';
              if (err) {
                messageHubService.postLoadError(err);
                reason = err.reason || err.statusText || err.message || '';
                $log.error(
                  `Unable to load assessment: ${reason || err.statusCode}`,
                  err,
                );
              }
              appAlert.error(
                `Could not start the assessment at this time${reason && ` (${reason})`}. You may need to log out and reload this page. To log out, access the menu on the top left of the page.`,
              );
            })
            .finally(() => {
              loadAssessmentResultPromise = null;
            });

          return loadAssessmentResultPromise;
        }

        function startAssessmentResult() {
          return assessmentResultsService
            .start(process.assessmentResult.id)
            .then(() => startTiming())
            .catch((err) => {
              if (err) {
                const code =
                  err.status || err.statusCode || err.statusText || err.message;
                const msg = `Unable to start assessment: ${code}`;
                $log.error(msg, JSON.stringify(err));
              }

              messageHubService.postLoadError(err);
              if ($window.Sentry) {
                $window.Sentry.setContext({
                  responseError: err,
                  cookie: $document.cookie,
                  assessmentResult: process.assessmentResult,
                });
              }
              appAlert.error('Could not start the assessment at this time.');
              return $q.reject();
            });
        }

        function startTiming() {
          if (process.timer && !process.timer.isRunning()) {
            process.timer.start(process.assessmentResult);
          }
          if (!process._heartbeat) {
            _setHeartbeat();
          }
          if (!process._focusTracking) {
            process._focusTracking = true;
            on('document:focus', () => {
              process.background = false;
              _heartbeat();
            });
            on('document:blur', () => {
              // delay marking as background until after the event has been fully processed
              $timeout(
                () => {
                  process.background = true;
                },
                1,
                false,
              );
            });
            on('idleDetection', ($event, isIdle) => {
              process.isIdle = isIdle;
              _heartbeat();
            });
          }
        }

        function _clearHeartbeat() {
          _heartbeat();
          $interval.cancel(process._heartbeat);
        }

        function _setHeartbeat() {
          process._heartbeat = $interval(_heartbeat, heartbeatInterval * 1000);
        }

        function _heartbeat() {
          const stage = getCurrentStage();
          if (
            stage &&
            stage.solution &&
            !process.isIdle &&
            !process.restricted
          ) {
            const { solution } = stage;
            const opts = {
              data: {
                background: process.background,
              },
            };

            Solution.heartbeat(solution, opts);
          }
        }

        function submitAssessment() {
          const dialog = assessmentSubmittingDialog
            .open(process.assessmentResult)
            .then(process.performPostAssessmentAction);
          const submit = assessmentResultsService
            .submit(process.assessmentResult)
            .then(() => {
              cleanupOnSubmit();
              if (!$state.is('assessments.review')) {
                $state.go('assessments.review');
              }
            })
            .catch((resp) => {
              assessmentSubmittingDialog.close();
              messageHubService.postError(ERROR_CONTEXT.submitAssessment, resp);
              return appAlert.modelError(resp);
            });

          return $q.all([dialog, submit]).then(() => ({
            redirected: process.assessmentResult.hasPostAssessmentRedirect(),
            assessmentResult: process.assessmentResult,
          }));
        }

        function _timerRanOut() {
          assessmentSubmittingDialog
            .open(process.assessmentResult, true)
            .then(process.performPostAssessmentAction);
          AssessmentResult.find(process.assessmentResult.id, {
            bypassCache: true,
          })
            .then((result) => {
              // the server should auto-submit this, but if not we'll submit it.
              if (!result.isSubmitted()) {
                result.closedReason = 'timer_ran_out';
                return assessmentResultsService.submit(result);
              }
            })
            // because the server is handling this, we can ignore submission errors
            .catch(() => null)
            .finally(() => {
              $state.go('assessments.review');
            });
        }

        //-------------------------------------------------------
        // Stage Management
        //-------------------------------------------------------

        /**
         * Creates a single, ordered array of all the stages in this application
         * @returns {Array.<Stage>}
         */
        function allStages() {
          return [].concat(stages.welcome, stages.challenges, stages.review);
        }

        /**
         * Returns the current stage based on the current $state (or welcome if none is found)
         * @returns {Stage}
         */
        function getCurrentStage() {
          // default to welcome if not on a valid URL
          let current = stages.welcome;
          allStages().forEach(function (stage) {
            if ($state.includes(stage.state, stage.stateParams)) {
              current = stage;
            }
          });
          return current;
        }

        function _getNextStage() {
          let currentStage;
          let nextStage = null;

          allStages().forEach(function (stage) {
            if (!currentStage) {
              if ($state.includes(stage.state, stage.stateParams)) {
                currentStage = stage;
              }
            } else if (!nextStage || nextStage.isCompleted()) {
              nextStage = stage;
            }
          });
          return nextStage;
        }

        function _getPreviousStage() {
          let currentStage = getCurrentStage();
          let stages = allStages();
          let idx = stages.findIndex((stage) => stage === currentStage);
          if (idx > 0) {
            return stages[--idx];
          }
        }

        /**
         * Returns to the previous challenge
         */
        function previous() {
          let previousStage = _getPreviousStage();
          refreshStageStatuses();
          if (previousStage) {
            $state.go(previousStage.state, previousStage.stateParams);
          }
        }

        /**
         * Skips to the next stage in the process, useful for calling after completing a stage
         */
        function next() {
          if (process.isFinished()) {
            $state
              .go('assessments.review')
              .then(() => (process.started = true));
            return;
          }
          let nextStage = _getNextStage();
          refreshStageStatuses();
          if (nextStage) {
            $state.go(nextStage.state, nextStage.stateParams).then(() => {
              process.started = true;
            });
          }
        }

        function nextIsChallenge() {
          if (process.isFinished()) return false;
          let nextStage = _getNextStage();
          return nextStage && !!nextStage.challengeId;
        }

        /**
         * Loops through the assessment's ChallengeItems and creates a Stage for each one.
         *
         * If the AssessmentResult is loaded, then this will also configure things like
         * the Challenge title and the current status based on the solution.
         *
         */
        function setupChallengeStages() {
          const ar = process.assessmentResult;
          const solutions = ar
            ? Solution.filter({ assessmentResultId: ar.id })
            : [];
          const challengeItems = process.challengeItems;

          stages.challenges = challengeItems
            .sort((a, b) => a.position - b.position)
            .map(function (challengeItem) {
              const challengeId = challengeItem.challengeId;
              const challengeStage = new Stage(
                'assessments.challenges',
                { challengeId },
                ar,
              );

              challengeStage.challengeId = challengeId;
              challengeStage.required = challengeItem.required;
              challengeStage.position = challengeItem.position;

              if (process.assessmentResult) {
                refreshStage(
                  challengeStage,
                  solutions.find((s) => s.challengeId === challengeId),
                );
              }

              return challengeStage;
            });

          process.challengeCount = stages.challenges.length;
        }

        /**
         * Updates the status on every stage
         */
        function refreshStageStatuses() {
          angular.forEach(allStages(), (stage) => refreshStage(stage));
        }

        /**
         * Updates the status on a challenge stage
         *
         * @param stage the stage to refresh
         * @param solution Optional solution to set on the stage, used for determining status
         */
        function refreshStage(stage, solution) {
          if (stage.refresh) {
            stage.refresh();
          } else if (stage.challengeId) {
            const challenge = (stage.challenge =
              stage.challenge || (solution && solution.challenge));

            if (challenge) {
              stage.state = `assessments.challenges.${challenge.typeState()}`;
            }

            if (solution) {
              stage.solution = solution;
            } else {
              solution = stage.solution;
            }

            let status = STAGE_STATUSES.NEW;
            const _setStatus = ({ failed, completed }) => {
              status = failed
                ? STAGE_STATUSES.FAILED
                : completed
                  ? STAGE_STATUSES.COMPLETED
                  : status;
            };

            if (solution) {
              status = STAGE_STATUSES.PARTIAL;
              const attempted = solution.attempted();
              const allowPartialPassing = !challenge.enforceAllTestsOnPublish;

              if (solution.isAdvancedCode()) {
                const candidateTestsDisabled = !challenge.candidateTestsEnabled;

                if (challenge.submissionFeedback) {
                  const submissionFailed =
                    solution.updateSubmissionFailedMessages().length;
                  const submissionPassed =
                    solution.isSubmitted() && !submissionFailed;
                  // If we have submission feedback...
                  if (submissionPassed) {
                    // ...and we are in the "passed" state, always mark as passed (ignore candidate tests)
                    _setStatus({ completed: true });
                  } else if (candidateTestsDisabled) {
                    // ...and no candidate tests, then we use submission feedback for flagging failures
                    _setStatus({
                      failed: solution.isSubmitted() && submissionFailed,
                    });
                  } else {
                    // ...and candidate tests, then we use candidate tests for failed/partial
                    _setStatus({
                      // uses candidate test counts for failed flag, falls back to submission
                      // feedback for completed
                      failed: attempted && !solution.passed(),
                    });
                  }
                } else {
                  _setStatus({
                    failed:
                      solution.isSubmitted() && !solution.isSubmissionSuccess(),
                    // solution was submitted at all (assumes it'll get processed in the background)
                    completed: solution.isSubmissionSuccess(),
                  });
                }
              } else {
                // for other types, only set failed/completed flags if the code was attempted
                if (attempted) {
                  if (solution.isCode()) {
                    _setStatus({
                      // no passed test cases at all (probably a runtime error)
                      failed: !solution.passedTests(),
                      // passed all tests or at least some if not expected to pass all
                      completed: allowPartialPassing || solution.passed(),
                    });
                  } else if (solution.isQuiz()) {
                    // for quizzes, we only mark partial/completed
                    _setStatus({
                      // either everything is answered, or time is up
                      completed:
                        solution.allAnswered() || !solution.canStillAnswer(),
                    });
                  }
                }
              }

              if (solution.expired() && status !== STAGE_STATUSES.COMPLETED) {
                // always mark incomplete expired challenges as expired, because there's nothing the
                // candidate can do at this point.
                status = STAGE_STATUSES.EXPIRED;
              }
            }
            stage.status = status;
          }
        }

        function addSolution(solution) {
          refreshStage(getCurrentStage(), solution);
        }

        function restart() {
          if (process.assessmentResult) {
            return AssessmentResult.openWithAlert(
              process.assessmentResult,
            ).then(() => goToWelcomeAndReload());
          }
          return $q.reject();
        }

        function reset(resetAll) {
          return AssessmentResult.resetWithAlert(process.assessmentResult, {
            data: { resetAll },
          }).then(() => goToWelcomeAndReload());
        }

        function performPostAssessmentAction() {
          const ar = process.assessmentResult;
          if (ar.retrySchedule || ar.nextAssessmentId) {
            // For now we are hard loading the page since there are some potential errors causing
            // goToWelcomeAndReload to fail (usually caused by a phantom challenges GET which I can't track down.
            // TODO: fix challenges API call and revert
            $window.location = `/assess/${ar.retrySchedule ? ar.assessmentId : ar.nextAssessmentId}`;
            // goToWelcomeAndReload({
            // 	assessmentId: ar.retrySchedule ? ar.assessmentId : ar.nextAssessmentId,
            // });
          } else if (ar.$redirectUrl) {
            // HACK: enables sending the candidate to the start screen
            // TODO: convert to valid feature later
            // TODO: also add close-tab as an option
            // TODO: allow logging out as an option on close tab and redirect
            if (/^http:\/\/logout$/.test(ar.$redirectUrl)) {
              process.logout();
            } else {
              $window.location = safeUrl(ar.$redirectUrl);
            }
          }
        }

        function goToWelcomeAndReload(params = null) {
          allModels.ejectAll({
            exclude: LOGOUT_EJECT_EXCLUDES,
          });
          $state.go('assessments.welcome', params, { reload: true });
        }

        //-------------------------------------------------------
        // Event Management
        //-------------------------------------------------------

        function watch(val, listener) {
          return rootScopeListeners.push($rootScope.$watch(val, listener));
        }

        function watchCollection(val, listener) {
          return rootScopeListeners.push(
            $rootScope.$watchCollection(val, listener),
          );
        }

        function on(event, listener) {
          rootScopeListeners.push($rootScope.$on(event, listener));
        }

        function destroy() {
          leavePresence();
          angular.forEach(rootScopeListeners, (off) => off());
          warnBeforeClosingListener();
        }

        function cleanupOnSubmit() {
          _clearHeartbeat();
          if (process.liveInterview) {
            // after we submit the assessment, there's no need to store anything about it in
            // Firebase (interviewers will automatically be disconnected)
            process.liveInterview.destroy();
          }
          process.timer && process.timer.stop();
        }

        function whenReady() {
          return readyDeferred.promise;
        }

        function whenAssessmentResultReady() {
          return assessmentResultDeferred.promise;
        }
      }
    },
  );
